let apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
if (apiBaseUrl && !apiBaseUrl.startsWith("http")) {
    apiBaseUrl = `https://${apiBaseUrl}`;
}
export const API_BASE = apiBaseUrl;

async function readApiResponse(res, fallbackMessage) {
    const contentType = res.headers.get("content-type") || "";

    try {
        if (contentType.includes("application/json")) {
            return await res.json();
        }

        const text = await res.text();
        if (text && text.trim().startsWith("<")) {
            return {
                message: `${fallbackMessage} The backend may need to be restarted.`,
                raw: text,
            };
        }


        return { message: text || fallbackMessage };
    } catch {
        return { message: fallbackMessage };
    }
}

// ── Books ──────────────────────────────────────────────
export async function fetchBooks(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const res = await fetch(`/api/books?${params}`);
    if (!res.ok) throw new Error("Failed to fetch books");
    return res.json();
}

export async function fetchBookById(id) {
    const res = await fetch(`/api/books/${id}`);
    if (!res.ok) throw new Error("Failed to fetch book");
    return res.json();
}

export async function fetchCategories() {
    const res = await fetch(`/api/books/categories`);
    if (!res.ok) throw new Error("Failed to fetch categories");
    return res.json();
}

// --------- 25-6-26 update commit #36
export async function updateCategory(token, id, name) {
    const res = await fetch(`/api/books/categories/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ name })
    });
    const data = await readApiResponse(res, "Failed to update category.");
    if (!res.ok) throw new Error(data.message || "Failed to update category");
    return data;
}

export async function createCategory(token, name) {
    const res = await fetch(`/api/books/categories`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ name })
    });
    const data = await readApiResponse(res, "Failed to create category.");
    if (!res.ok) throw new Error(data.message || "Failed to create category");
    return data;
}

export async function reorderCategories(token, orderedIds) {
    const res = await fetch(`/api/books/categories/reorder`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ orderedIds })
    });
    const data = await readApiResponse(res, "Failed to reorder categories.");
    if (!res.ok) throw new Error(data.message || "Failed to reorder categories");
    return data;
}

export async function deleteCategory(token, id) {
    const res = await fetch(`/api/books/categories/${id}`, {
        method: "DELETE",
        headers: {
            "Authorization": `Bearer ${token}`
        }
    });
    const data = await readApiResponse(res, "Failed to delete category.");
    if (!res.ok) throw new Error(data.message || "Failed to delete category");
    return data;
}

//------------------------


// ── Authors ─────────────────────────────────────────────
export async function fetchAuthors() {
    const res = await fetch(`/api/authors`);
    const data = await readApiResponse(res, "Failed to fetch authors.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch authors");
    return data;
}

export async function createAuthor(token, formData) {
    const res = await fetch(`/api/authors`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`
        },
        body: formData,
    });
    const data = await readApiResponse(res, "Failed to create author.");
    if (!res.ok) throw new Error(data.message || "Failed to create author");
    return data;
}

export async function deleteAuthor(token, id) {
    const res = await fetch(`/api/authors/${id}`, {
        method: "DELETE",
        headers: {
            "Authorization": `Bearer ${token}`
        }
    });
    const data = await readApiResponse(res, "Failed to delete author.");
    if (!res.ok) throw new Error(data.message || "Failed to delete author");
    return data;
}

export async function fetchHomepageSections() {
    const res = await fetch(`/api/homepage-sections`);
    const data = await readApiResponse(res, "Failed to fetch homepage sections.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch homepage sections");
    return data;
}

export async function updateHomepageSections(token, sections) {
    const res = await fetch(`/api/homepage-sections`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sections }),
    });
    const data = await readApiResponse(res, "Failed to update homepage sections.");
    if (!res.ok) throw new Error(data.message || "Failed to update homepage sections");
    return data;
}

// ── Banners ─────────────────────────────────────────────
export async function fetchBanners() {
    const res = await fetch(`/api/banners`);
    const data = await readApiResponse(res, "Failed to fetch banners.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch banners");
    return data;
}

export async function uploadBanner(token, file, href = "/store") {
    const formData = new FormData();
    formData.append("bannerImage", file);
    formData.append("href", href);

    const res = await fetch(`/api/banners`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
    });
    const data = await readApiResponse(res, "Failed to upload banner.");
    if (!res.ok) throw new Error(data.message || "Failed to upload banner");
    return data;
}

export async function deleteBanner(token, bannerId) {
    const res = await fetch(`/api/banners/${bannerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await readApiResponse(res, "Failed to delete banner.");
    if (!res.ok) throw new Error(data.message || "Failed to delete banner");
    return data;
}

export async function reorderBanners(token, orderedIds) {
    const res = await fetch(`/api/banners/reorder`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderedIds }),
    });
    const data = await readApiResponse(res, "Failed to reorder banners.");
    if (!res.ok) throw new Error(data.message || "Failed to reorder banners");
    return data;
}

export async function createBook(token, formData) {
    const res = await fetch(`/api/books`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`
        },
        body: formData,
    });
    const data = await readApiResponse(res, "Failed to create book.");
    if (!res.ok) throw new Error(data.error || data.message || "Failed to create book");
    return data;
}

export async function updateBook(token, bookId, formData) {
    const res = await fetch(`/api/books/${bookId}`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${token}`
        },
        body: formData,
    });
    const data = await readApiResponse(res, "Failed to update book.");
    if (!res.ok) throw new Error(data.error || data.message || "Failed to update book");
    return data;
}

export async function deleteBook(token, bookId) {
    const res = await fetch(`/api/books/${bookId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to delete book");
    return data;
}

export async function fetchCoupons(token) {
    const res = await fetch(`/api/coupons`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await readApiResponse(res, "Failed to fetch coupons.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch coupons");
    return data;
}

export async function createCoupon(token, couponData) {
    const res = await fetch(`/api/coupons`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(couponData),
    });
    const data = await readApiResponse(res, "Failed to create coupon.");
    if (!res.ok) throw new Error(data.message || "Failed to create coupon");
    return data;
}

export async function deleteCoupon(token, couponId) {
    const res = await fetch(`/api/coupons/${couponId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const data = await readApiResponse(res, "Failed to delete coupon.");
    if (!res.ok) throw new Error(data.message || "Failed to delete coupon");
    return data;
}


export async function updateCoupon(token, couponId, couponData) {
    const res = await fetch(`/api/coupons/${couponId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(couponData),
    });
    const data = await readApiResponse(res, "Failed to update coupon.");
    if (!res.ok) throw new Error(data.message || "Failed to update coupon");
    return data;
}


export async function validateCoupon(token, couponData) {
    const res = await fetch(`/api/coupons/validate`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(couponData),
    });

    const data = await readApiResponse(res, "Failed to validate coupon.");
    if (!res.ok) throw new Error(data.message || "Failed to validate coupon");
    return data;
}

// ── Auth ───────────────────────────────────────────────
export async function fetchUsers(token) {
    const res = await fetch(`/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await readApiResponse(res, "Failed to fetch users.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch users");
    return data;
}

export async function registerUser({ name, email, password }) {
    const res = await fetch(`/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Registration failed");
    return data;
}

export async function loginUser({ email, password }) {
    const res = await fetch(`/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Login failed");
    return data;
}

export async function googleAuthLogin(credential) {
    const res = await fetch(`/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Google sign-in failed");
    return data;
}

export async function fetchProfile(token) {
    const res = await fetch(`/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to fetch profile");
    return data;
}

export async function addAddress(token, addressData) {
    const res = await fetch(`/api/users/address`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(addressData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to add address");
    return data; // returns updated addresses array
}

export async function updateAddress(token, addressId, addressData) {
    const res = await fetch(`/api/users/address/${addressId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(addressData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to update address");
    return data;
}

export async function deleteAddress(token, addressId) {
    const res = await fetch(`/api/users/address/${addressId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to delete address");
    return data;
}

// ── Newsletter ─────────────────────────────────────────
export async function subscribeNewsletter({ name, email }) {
    const res = await fetch(`/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Subscription failed");
    return data;
}

// ── Contact ────────────────────────────────────────────
export async function submitContact({ name, email, phone, message }) {
    const res = await fetch(`/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, message }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to send message");
    return data;
}

// ── Orders ───────────────────────────────────────────────
export async function createOrder(token, orderData) {
    const res = await fetch(`/api/orders`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
    });

    const contentType = res.headers.get("content-type") || "";
    let data;
    try {
        if (contentType.includes("application/json")) {
            data = await res.json();
        } else {
            data = await res.text();
        }
    } catch {
        data = null;
    }

    if (!res.ok) {
        const message =
            (data && typeof data === "object" && data.message) ||
            "Failed to place order";
        throw new Error(message);
    }
    return data;
}

export async function fetchMyOrders(token) {
    const res = await fetch(`/api/orders/myorders`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const contentType = res.headers.get("content-type") || "";
    let data;
    try {
        if (contentType.includes("application/json")) {
            data = await res.json();
        } else {
            data = await res.text();
        }
    } catch {
        data = null;
    }

    if (!res.ok) {
        const message =
            (data && typeof data === "object" && data.message) ||
            "Failed to fetch orders";
        throw new Error(message);
    }
    return data;
}

export async function fetchOrderById(id, token) {
    const res = await fetch(`/api/orders/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const contentType = res.headers.get("content-type") || "";
    let data;
    try {
        if (contentType.includes("application/json")) {
            data = await res.json();
        } else {
            data = await res.text();
        }
    } catch {
        data = null;
    }

    if (!res.ok) {
        const message =
            (data && typeof data === "object" && data.message) ||
            "Failed to fetch order";
        throw new Error(message);
    }
    return data;
}

export async function fetchAllOrders(token) {
    const res = await fetch(`/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to fetch all orders");
    return data;
}

export async function updateOrderStatus(token, orderId, { deliveryStatus, trackingId, postOffice }) {
    const res = await fetch(`/api/orders/${orderId}/status`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ deliveryStatus, trackingId, postOffice }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to update order status");
    return data;
}

// ── CCAvenue Payment ─────────────────────────────────────
export async function deleteOrder(token, orderId) {
    const res = await fetch(`/api/orders/${orderId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to delete order");
    return data;
}

export async function initiateCCAvenue(token, orderData) {
    const res = await fetch(`/api/payment/ccavenue/initiate`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
    });

    const contentType = res.headers.get("content-type") || "";
    let data;
    try {
        if (contentType.includes("application/json")) {
            data = await res.json();
        } else {
            data = await res.text();
        }
    } catch {
        data = null;
    }

    if (!res.ok) {
        const message =
            (data && typeof data === "object" && data.message) ||
            "Failed to initiate payment";
        throw new Error(message);
    }
    return data;
}




// ── Dealers ─────────────────────────────────────────────
export async function fetchDealersGrouped() {
    const res = await fetch(`/api/dealers/grouped`);
    const data = await readApiResponse(res, "Failed to fetch dealers.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch dealers");
    return data;
}

export async function fetchDealers() {
    const res = await fetch(`/api/dealers`);
    const data = await readApiResponse(res, "Failed to fetch dealers.");
    if (!res.ok) throw new Error(data.message || "Failed to fetch dealers");
    return data;
}

export async function createDealer(token, dealerData) {
    const res = await fetch(`/api/dealers`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dealerData),
    });
    const data = await readApiResponse(res, "Failed to create dealer.");
    if (!res.ok) throw new Error(data.message || "Failed to create dealer");
    return data;
}

export async function updateDealer(token, id, dealerData) {
    const res = await fetch(`/api/dealers/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dealerData),
    });
    const data = await readApiResponse(res, "Failed to update dealer.");
    if (!res.ok) throw new Error(data.message || "Failed to update dealer");
    return data;
}

export async function deleteDealer(token, id) {
    const res = await fetch(`/api/dealers/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await readApiResponse(res, "Failed to delete dealer.");
    if (!res.ok) throw new Error(data.message || "Failed to delete dealer");
    return data;
}

